import { TFunction } from 'react-i18next'

import type { BlueprintFamilyDurationBucketLabel, BlueprintFamilyDurationDistributionApiItem } from '../../../../api/blueprintFamilies/durationDistribution'
import type { DurationBucket } from '../TypicalDurationsCard/TypicalDurationsCard'

// Duration-distribution wire label → stable FE bucket key + display copy. The BE always
// ships all 7 buckets in this order, zero-filled where empty (GR-2916).
const DURATION_BUCKETS: Record<BlueprintFamilyDurationBucketLabel, { key: string; labelKey: string }> = {
  D1: { key: 'd1', labelKey: 'market-analytics:duration_bucket_d1' },
  D2_3: { key: 'd2_3', labelKey: 'market-analytics:duration_bucket_d2_3' },
  D4_6: { key: 'd4_6', labelKey: 'market-analytics:duration_bucket_d4_6' },
  D7_13: { key: 'd7_13', labelKey: 'market-analytics:duration_bucket_d7_13' },
  D14_27: { key: 'd14_27', labelKey: 'market-analytics:duration_bucket_d14_27' },
  D28_31: { key: 'd28_31', labelKey: 'market-analytics:duration_bucket_d28_31' },
  D32_PLUS: { key: 'd32plus', labelKey: 'market-analytics:duration_bucket_d32_plus' },
}

/**
 * Wire buckets → `TypicalDurationsCard` rows, shared by the blueprint-family,
 * event-category and event-type pages so one histogram can't label its rows
 * differently from another.
 *
 * Returns undefined when there is nothing to plot — no response, or every bucket at
 * zero — which is what hides the card rather than drawing seven empty tracks. Note the
 * emptiness test is per bucket: the buckets overlap (a game counts in every length it
 * ran), so summing them would not be the entity's game count and must not be used as a
 * denominator anywhere.
 *
 * Takes `t` rather than calling the hook so it stays usable from memoized render paths.
 */
export function toDurationBuckets(buckets: BlueprintFamilyDurationDistributionApiItem[] | undefined, t: TFunction): DurationBucket[] | undefined {
  if (!buckets) return undefined
  if (!buckets.some((bucket) => bucket.gamesCount > 0)) return undefined

  return buckets.map((bucket) => ({
    key: DURATION_BUCKETS[bucket.label]?.key ?? bucket.label,
    label: t(DURATION_BUCKETS[bucket.label]?.labelKey ?? bucket.label),
    gamesCount: bucket.gamesCount,
  }))
}
